import EditIcon from '@mui/icons-material/Edit';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import { Chip } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { baseCell, currencyCell } from 'components/cells';
import {
  numberFilterOperators,
  filterOperators,
  dateFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { TFunction } from 'i18next';

export function AggregatedUnapprovedMovementColumns(
  t: TFunction,
  isTechnicianCoordinatorRole: boolean,
  handleEdit?: any,
  types?: string[]
): GridColDef[] {
  const translationPrefix = 'entities.movement';

  const columns: GridColDef[] | any[] = [
    {
      field: 'actions',
      flex: 50,
      align: 'right',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        return [
          {
            label: t('actions.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => handleEdit(params.row),
            visible: !isTechnicianCoordinatorRole
          }
        ];
      }
    },
    {
      field: 'expected_equal',
      headerName: '!',
      filterable: false,
      sortable: false,
      type: 'boolean',
      width: 50,
      renderCell: (params: GridRenderCellParams<any>) =>
        params.row.remaining_quantity &&
        !params.value && (
          <Chip sx={{ background: 'transparent' }} icon={<PriorityHigh color="warning" />} />
        )
    },
    {
      field: 'total_quantity',
      width: 100,
      headerName: t(`${translationPrefix}.moved_quantity`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      renderCell: baseCell
    },
    {
      field: 'remaining_quantity',
      headerName: t(`entities.order_line.remaining_quantity`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      renderCell: baseCell
    },
    {
      field: 'product',
      headerName: t(`global.product`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'product_notes',
      headerName: t(`entities.product.product_notes`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'order_notes',
      headerName: t(`entities.product.purchase-request-notes`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'contract',
      headerName: t(`global.contract`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 75,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'timestamp',
      headerName: t(`${translationPrefix}.timestamp`),
      filterOperators: dateFilterOperators,
      width: 125,
      renderCell: baseCell
    },
    {
      field: 'price',
      headerName: t(`${translationPrefix}.price`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      width: 100,
      renderCell: currencyCell
    },
    {
      field: 'unit_price',
      headerName: t(`${translationPrefix}.unit_price`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      width: 100,
      renderCell: currencyCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    },

    {
      field: 'movement_type',
      headerName: t(`${translationPrefix}.type`),
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: types || [],
      width: 50,
      filterOperators: singleSelectFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'order_code',
      headerName: t(`entities.movement.order_code`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'transport_document_code',
      headerName: t(`${translationPrefix}.transport_document`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'transport_document_date',
      headerName: t(`${translationPrefix}.transport_document_date`),
      filterable: true,
      filterOperators: dateFilterOperators,
      sortable: true,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'created_by',
      headerName: t(`${translationPrefix}.created_by`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 100,
      renderCell: baseCell
    }
  ];

  return columns;
}
